/**
 * Project "hike highlights" (waypoints / POIs) onto an elevation profile line.
 *
 * For each highlight this computes, via turf `nearestPointOnLine`:
 *  - its distance ALONG the line (metres) — where to place it on the x-axis;
 *  - its perpendicular distance FROM the line (metres) — used both to filter
 *    out highlights that are nowhere near the route and to let callers show how
 *    far off-route a highlight is;
 *  - the snapped `[lng, lat]` on the line.
 *
 * Highlights farther than `maxDistanceFromLineMeters` from the line are
 * excluded (they aren't really on this route).
 */
import {
  lineString as turfLineString,
  point as turfPoint,
  nearestPointOnLine,
} from '@turf/turf';

export interface HighlightInput {
  id: string | number;
  name: string;
  lon: number;
  lat: number;
  icon?: string;
  /** Any extra caller data is preserved on the output via the generic. */
  [key: string]: unknown;
}

export interface ProjectedHighlight {
  id: string | number;
  name: string;
  icon?: string;
  /** Distance along the line, in metres, where the highlight snaps. */
  distanceMeters: number;
  /** Perpendicular distance from the line to the highlight, in metres. */
  distanceFromLineMeters: number;
  /** `[lng, lat]` snapped onto the line. */
  snappedLngLat: [number, number];
}

export interface ProjectHighlightsOptions {
  /** Drop highlights farther than this from the line (metres). Default 250. */
  maxDistanceFromLineMeters?: number;
  /**
   * Optional true route length (metres). Highlights whose snapped distance
   * exceeds this are clamped to it (guards against float drift past the end).
   */
  maxDistanceMeters?: number;
}

/**
 * Project highlights onto `lineCoords` (`[lon, lat, …][]`, GeoJSON order).
 * Returns projected highlights sorted by distance along the line, excluding any
 * beyond `maxDistanceFromLineMeters`.
 */
export function projectHighlightsOntoLine(
  lineCoords: number[][],
  highlights: HighlightInput[],
  options: ProjectHighlightsOptions = {}
): ProjectedHighlight[] {
  const maxFromLine = options.maxDistanceFromLineMeters ?? 250;

  if (!lineCoords || lineCoords.length < 2 || !highlights || highlights.length === 0) {
    return [];
  }

  // turf requires 2D coords; strip any elevation.
  const line = turfLineString(lineCoords.map((c) => [c[0], c[1]]));

  const projected: ProjectedHighlight[] = [];

  for (const h of highlights) {
    if (h == null || typeof h.lon !== 'number' || typeof h.lat !== 'number') {
      continue;
    }

    const snapped = nearestPointOnLine(line, turfPoint([h.lon, h.lat]), {
      units: 'meters',
    });

    const distanceFromLineMeters = snapped.properties.dist ?? Infinity;
    if (distanceFromLineMeters > maxFromLine) continue;

    let distanceMeters = snapped.properties.location ?? 0;
    if (options.maxDistanceMeters != null && distanceMeters > options.maxDistanceMeters) {
      distanceMeters = options.maxDistanceMeters;
    }

    const [lng, lat] = snapped.geometry.coordinates;

    projected.push({
      id: h.id,
      name: h.name,
      icon: h.icon,
      distanceMeters,
      distanceFromLineMeters,
      snappedLngLat: [lng, lat],
    });
  }

  projected.sort((a, b) => a.distanceMeters - b.distanceMeters);
  return projected;
}
